import type {
  AppealAct,
  AppealActAmounts,
  AppealAmountHead,
  AppealAmountsByAct,
  OiaRupees,
} from "../types"

/**
 * Display arithmetic over the backend's amount grids. NOTHING here decides a
 * figure: every amount the officer sees was computed by backend code (the
 * spine reducer, compute_change_effect). The frontend only adds up cells of a
 * grid it was given, for a total line, and never invents a cell.
 *
 * null is NOT zero (types.ts): a head the order never stated is null. A total
 * over only-null cells is null ("not stated"), not ₹0 — an order that says
 * nothing about penalty must not read as "penalty nil".
 */

export const ACTS: readonly AppealAct[] = ["cgst", "sgst", "igst", "cess"]
export const HEADS: readonly AppealAmountHead[] = ["tax", "interest", "penalty", "fee", "other"]

export const ACT_LABEL: Record<AppealAct, string> = {
  cgst: "CGST",
  sgst: "SGST",
  igst: "IGST",
  cess: "Cess",
}

export const HEAD_LABEL: Record<AppealAmountHead, string> = {
  tax: "Tax",
  interest: "Interest",
  penalty: "Penalty",
  fee: "Fee",
  other: "Other",
}

function add(a: OiaRupees, b: OiaRupees): OiaRupees {
  if (a === null || a === undefined) return b ?? null
  if (b === null || b === undefined) return a
  return a + b
}

/** Sum of one head across the Acts (null when no Act states it). */
export function headTotal(grid: AppealActAmounts | null | undefined, head: AppealAmountHead): OiaRupees {
  if (!grid) return null
  let total: OiaRupees = null
  for (const act of ACTS) total = add(total, grid[act]?.[head] ?? null)
  return total
}

/** Sum of every stated cell (null when nothing is stated). */
export function gridTotal(grid: AppealActAmounts | null | undefined): OiaRupees {
  if (!grid) return null
  let total: OiaRupees = null
  for (const head of HEADS) total = add(total, headTotal(grid, head))
  return total
}

export function byActTotal(amounts: AppealAmountsByAct | null | undefined): OiaRupees {
  if (!amounts) return null
  let total: OiaRupees = null
  for (const act of ACTS) total = add(total, amounts[act] ?? null)
  return total
}

/** Heads stated in either grid, in head order — rows worth showing. */
export function statedHeads(...grids: Array<AppealActAmounts | null | undefined>): AppealAmountHead[] {
  return HEADS.filter((head) => grids.some((g) => headTotal(g, head) !== null))
}

/** Act x head cells that carry a value in either grid. */
export function statedCells(
  ...grids: Array<AppealActAmounts | null | undefined>
): Array<{ act: AppealAct; head: AppealAmountHead }> {
  const cells: Array<{ act: AppealAct; head: AppealAmountHead }> = []
  for (const act of ACTS) {
    for (const head of HEADS) {
      if (grids.some((g) => g?.[act]?.[head] !== null && g?.[act]?.[head] !== undefined)) {
        cells.push({ act, head })
      }
    }
  }
  return cells
}

const INR = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 })

/** "₹ 3,20,500" ; null -> "—" (not stated). */
export function formatRupees(value: OiaRupees | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—"
  const sign = value < 0 ? "−" : ""
  return `${sign}₹ ${INR.format(Math.abs(value))}`
}

/** Signed change for a delta column: "+ ₹ 10,000" / "− ₹ 10,000" / "no change". */
export function formatDelta(before: OiaRupees, after: OiaRupees): string {
  if (before === null && after === null) return "—"
  const delta = (after ?? 0) - (before ?? 0)
  if (delta === 0) return "no change"
  return `${delta > 0 ? "+" : "−"} ₹ ${INR.format(Math.abs(delta))}`
}

/** after − before over stated cells; null when neither side states anything. */
export function deltaOf(before: OiaRupees, after: OiaRupees): number | null {
  if (before === null && after === null) return null
  return (after ?? 0) - (before ?? 0)
}
